import { Info } from "@components/icons"
import { IconWrapper } from "@components/icons/utils"
import colors from "@constants/colors"
import {
  getCarsMakeModelYearTrimUrl,
  getCarsMakeModelYearUrl,
} from "@utils/urls"
import { useRouter } from "next/router"
import React from "react"
import useGtmEvents from "@hooks/useGtmEvents"
import { useVehicleSelectionHandlers } from "./hooks/useVehicleSelectionHandlers"
import ModernVehicleSelector from "./ModernVehicleSelector"
import { TabsWithBorder, TabWithBorder } from "./TabsWithBorder"
import { Body, Container, Footer, Label, TabsContainer } from "./styles"
import { CarSelectorProps } from "./types"
import VehicleSection from "./VehicleSection"

// Helper function to determine if a car is classic or modern based on years
const isClassicCar = (car: any): boolean => {
  if (!car.years) return true
  const yearList = car.years.split(", ").map((y: string) => parseInt(y.trim()))
  const maxYear = Math.max(...yearList.filter((y: number) => !isNaN(y)))
  return maxYear < 2020
}

// Popular builds data
const classicPopularBuilds = [
  { label: "1988 Chevy Truck", make: "chevy", model: "truck", year: "1988" },
  { label: "1975 Ford Truck", make: "ford", model: "truck", year: "1975" },
  {
    label: "1955 VW Bug Sedan",
    make: "vw",
    model: "bug",
    year: "1955",
    type: "bug-sedan",
  },
]

// Moved to ModernVehicleSelector
// const modernPopularBuilds = [
//   {
//     label: "2024 Chevrolet Silverado 1500",
//     make: "chevrolet",
//     model: "silverado-1500",
//     year: "2024",
//   },
//   {
//     label: "2024 Ford F-150",
//     make: "ford",
//     model: "f-150",
//     year: "2024",
//   },
//   {
//     label: "2024 Toyota Tacoma",
//     make: "toyota",
//     model: "tacoma",
//     year: "2024",
//   },
// ]

const CarSelector: React.FC<CarSelectorProps> = ({
  isOpen,
  cars,
  makeModelOrder = {},
  resetPicker,
  onResetPicker,
  onRequestCloseMenu,
  module = "automotive",
}) => {
  const router = useRouter()
  const currentPath = router.asPath.split("?")[0].split("#")[0]
  const defaultActiveTab =
    currentPath === "/late-model" || currentPath === "/new-vehicle" ? 1 : 0
  const { onVehicleSelectInteraction } = useGtmEvents()
  // const layoutData = require("@data/layoutData")

  // Moved to ModernVehicleSelector
  // // Fetch upholstery cars when CarSelector mounts (ensures data on any page)
  // useFetchUpholsteryCars()

  // // Get upholstery cars for modern section
  // const {
  //   modernCars: upholsteryModernCars,
  //   isUsingUpholsteryCars,
  //   upholsteryCars,
  // } = useUpholsteryCarsForCarSelector()

  // // Hook to save vehicle selection to store
  // const { saveVehicle } = useSaveVehicleToStore()

  const filteredCars = cars.filter((car) => car.module == module)
  const classicCars = filteredCars.filter(isClassicCar)

  // Moved to ModernVehicleSelector
  // // Use upholstery cars for modern cars if available, otherwise fall back to regular cars
  // const modernCars = React.useMemo(() => {
  //   if (upholsteryModernCars.length > 0) {
  //     return upholsteryModernCars
  //   }
  //   // Fallback to regular cars if upholstery cars not loaded
  //   return filteredCars.filter((car) => !isClassicCar(car))
  // }, [upholsteryModernCars, filteredCars])

  // Classic cars state
  const [classicMake, setClassicMake] = React.useState<string>("")
  const [classicModel, setClassicModel] = React.useState<string>("")
  const [classicTrim, setClassicTrim] = React.useState<string>("")
  const [classicYear, setClassicYear] = React.useState<string>("")
  const [classicModels, setClassicModels] = React.useState<Array<any>>([])
  const [classicTrims, setClassicTrims] = React.useState<Array<any>>([])
  const [classicYears, setClassicYears] = React.useState<Array<any>>([])

  // Moved to ModernVehicleSelector
  // // Modern cars state
  // const [modernMake, setModernMake] = React.useState<string>("")
  // const [modernModel, setModernModel] = React.useState<string>("")
  // const [modernTrim, setModernTrim] = React.useState<string>("")
  // const [modernYear, setModernYear] = React.useState<string>("")
  // const [modernModels, setModernModels] = React.useState<Array<any>>([])
  // const [modernTrims, setModernTrims] = React.useState<Array<any>>([])
  // const [modernYears, setModernYears] = React.useState<Array<any>>([])
  // const [modernLoading, setModernLoading] = React.useState<boolean>(false)

  // Tab state
  const [activeTab, setActiveTab] = React.useState<number>(defaultActiveTab)

  // Classic vehicle selection handlers
  const {
    makes: classicMakes,
    onSelectMake: onClassicSelectMakeBase,
    onSelectModel: onClassicSelectModelBase,
    onSelectTrim: onClassicSelectTrimBase,
  } = useVehicleSelectionHandlers({
    cars: classicCars,
    selectedMake: classicMake,
    selectedModel: classicModel,
    selectedTrim: classicTrim,
    setMake: setClassicMake,
    setModel: setClassicModel,
    setTrim: setClassicTrim,
    setYear: setClassicYear,
    setModels: setClassicModels,
    setTrims: setClassicTrims,
    setYears: setClassicYears,
    isModern: false,
  })

  const onClassicSelectMake = (value: string) => {
    onClassicSelectMakeBase(value)
    if (value) {
      onVehicleSelectInteraction("vehicle_select_make", {
        flow: "shop_by_vehicle",
        source: "navbar",
        vehicle_type: "classic",
        make: value,
      })
    }
  }

  const onClassicSelectModel = (value: string) => {
    onClassicSelectModelBase(value)
    if (value) {
      onVehicleSelectInteraction("vehicle_select_model", {
        flow: "shop_by_vehicle",
        source: "navbar",
        vehicle_type: "classic",
        make: classicMake,
        model: value,
      })
    }
  }

  const onClassicSelectTrim = (value: string) => {
    onClassicSelectTrimBase(value)
    if (value) {
      onVehicleSelectInteraction("vehicle_dropdown_open", {
        flow: "shop_by_vehicle",
        source: "navbar",
        vehicle_type: "classic",
        field: "trim",
      })
    }
  }

  // Moved to ModernVehicleSelector
  // // Modern vehicle selection handlers
  // const {
  //   makes: modernMakes,
  //   onSelectMake: onModernSelectMake,
  //   onSelectModel: onModernSelectModel,
  //   onSelectTrim: onModernSelectTrim,
  // } = useVehicleSelectionHandlers({
  //   cars: modernCars,
  //   selectedMake: modernMake,
  //   selectedModel: modernModel,
  //   selectedTrim: modernTrim,
  //   setMake: setModernMake,
  //   setModel: setModernModel,
  //   setTrim: setModernTrim,
  //   setYear: setModernYear,
  //   setModels: setModernModels,
  //   setTrims: setModernTrims,
  //   setYears: setModernYears,
  //   isModern: true,
  // })

  const handleClassicBuild = () => {
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })

    if (classicMake && classicModel && classicYear) {
      onVehicleSelectInteraction("vehicle_click_build", {
        flow: "shop_by_vehicle",
        source: "navbar",
        vehicle_type: "classic",
        make: classicMake,
        model: classicModel,
        year: classicYear,
        ...(classicTrim ? { trim: classicTrim } : {}),
      })
    }

    if (classicTrim) {
      router.push(
        getCarsMakeModelYearTrimUrl(
          classicMake,
          classicModel,
          classicYear,
          classicTrim
        )
      )
    } else {
      router.push(
        getCarsMakeModelYearUrl(classicMake, classicModel, classicYear)
      )
    }
  }

  // Moved to ModernVehicleSelector
  // const handleModernBuild = async () => {
  //   // If using upholstery cars, save the selection to the store and redirect to upholstery
  //   if (isUsingUpholsteryCars && modernMake && modernModel && modernYear) {
  //     setModernLoading(true)
  //     try {
  //       const success = await saveVehicle(modernMake, modernModel, modernYear)

  //       if (success) {
  //         // Close menu before navigation (especially important if already on upholstery page)
  //         if (onRequestCloseMenu) {
  //           onRequestCloseMenu()
  //         }
  //         window.scrollTo({ top: 20, behavior: "smooth" })
  //         // Navigate to upholstery page - user will land in "Select Configuration" step
  //         router.push("/upholstery")
  //         return
  //       }
  //     } finally {
  //       setModernLoading(false)
  //     }
  //   }

  //   // Fallback: navigate to product page (for classic cars or if save failed)
  //   if (onRequestCloseMenu) {
  //     onRequestCloseMenu()
  //   }
  //   window.scrollTo({ top: 20, behavior: "smooth" })

  //   if (modernTrim) {
  //     router.push(
  //       getCarsMakeModelYearTrimUrl(
  //         modernMake,
  //         modernModel,
  //         modernYear,
  //         modernTrim
  //       )
  //     )
  //   } else {
  //     router.push(getCarsMakeModelYearUrl(modernMake, modernModel, modernYear))
  //   }
  // }

  // Now only used for classic builds (modern handled by ModernVehicleSelector)
  const handlePopularBuildClick = (build: any) => {
    // Fallback: navigate to product page (for classic cars)
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })
    const year_type = build.type ? `${build.year}/${build.type}` : build.year
    onVehicleSelectInteraction("vehicle_click_popular_build", {
      flow: "shop_by_vehicle",
      source: "navbar",
      vehicle_type: "classic",
      make: build.make,
      model: build.model,
      year: build.year,
      label: build.label,
    })
    router.push(getCarsMakeModelYearUrl(build.make, build.model, year_type))
  }

  // Moved to ModernVehicleSelector - old handlePopularBuildClick with modern support
  // const handlePopularBuildClick = async (
  //   build: any,
  //   variant: "classic" | "modern"
  // ) => {
  //   // If it's a modern build using upholstery cars, save to store and redirect to upholstery
  //   // Also check that upholstery cars are actually loaded (not just transformed)
  //   if (
  //     variant === "modern" &&
  //     isUsingUpholsteryCars &&
  //     upholsteryCars.length > 0
  //   ) {
  //     setModernLoading(true)
  //     try {
  //       const success = await saveVehicle(build.make, build.model, build.year)

  //       if (success) {
  //         // Close menu before navigation (especially important if already on upholstery page)
  //         if (onRequestCloseMenu) {
  //           onRequestCloseMenu()
  //         }
  //         window.scrollTo({ top: 20, behavior: "smooth" })
  //         // Navigate to upholstery page - user will land in "Select Configuration" step
  //         router.push("/upholstery")
  //         return
  //       } else {
  //         // Log why saveVehicle failed for debugging
  //         console.warn(
  //           `Failed to save vehicle to store: ${build.make}/${build.model}/${build.year}. Upholstery cars available: ${upholsteryCars.length}. Falling back to product page.`
  //         )
  //       }
  //     } catch (error) {
  //       console.error("Error saving vehicle to store:", error)
  //     } finally {
  //       setModernLoading(false)
  //     }
  //   }

  //   // Fallback: navigate to product page (for classic cars or if save failed)
  //   if (onRequestCloseMenu) {
  //     onRequestCloseMenu()
  //   }
  //   window.scrollTo({ top: 20, behavior: "smooth" })
  //   const year_type = build.type ? `${build.year}/${build.type}` : build.year
  //   router.push(getCarsMakeModelYearUrl(build.make, build.model, year_type))
  // }

  const reset = () => {
    setClassicMake("")
    setClassicModel("")
    setClassicTrim("")
    setClassicYear("")
    setClassicModels([])
    setClassicTrims([])
    setClassicYears([])

    // Moved to ModernVehicleSelector
    // setModernMake("")
    // setModernModel("")
    // setModernTrim("")
    // setModernYear("")
    // setModernModels([])
    // setModernTrims([])
    // setModernYears([])
  }

  React.useEffect(() => {
    if (!isOpen || resetPicker) {
      reset()
      setActiveTab(defaultActiveTab)

      if (resetPicker && onResetPicker) {
        onResetPicker()
      }
    }
  }, [isOpen, resetPicker, defaultActiveTab])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  return (
    <Container>
      <Body bgColor={colors.snow}>
        <Label fontColor={colors.obsidian}>
          <div>SHOP BY VEHICLE</div>
          <h1>LET'S START BY PROJECT</h1>
        </Label>
        <TabsContainer width="fit-content">
          <TabsWithBorder
            value={activeTab}
            onChange={handleTabChange}
            aria-label="vehicle type tabs"
            style={{ marginBottom: "20px" }}
          >
            <TabWithBorder label="1947-2017" />
            <TabWithBorder
              label={
                <span style={{ whiteSpace: "nowrap", textTransform: "none" }}>
                  2015 - PRESENT
                </span>
              }
            />
          </TabsWithBorder>

          {activeTab === 0 && (
            <VehicleSection
              title="1947-2017"
              variant="classic"
              makes={classicMakes}
              models={classicModels}
              trims={classicTrims}
              years={classicYears}
              selectedMake={classicMake}
              selectedModel={classicModel}
              selectedTrim={classicTrim}
              selectedYear={classicYear}
              onSelectMake={onClassicSelectMake}
              onSelectModel={onClassicSelectModel}
              onSelectTrim={onClassicSelectTrim}
              onSelectYear={setClassicYear}
              onBuild={handleClassicBuild}
              popularBuilds={classicPopularBuilds}
              onPopularBuildClick={handlePopularBuildClick}
            />
          )}
          {activeTab === 1 && (
            <ModernVehicleSelector
              cars={cars}
              module={module}
              onRequestCloseMenu={onRequestCloseMenu}
              resetPicker={resetPicker}
              onResetPicker={onResetPicker}
            />
          )}
          {/* Old code - using ModernVehicleSelector component instead
        {activeTab === 1 && (
          <VehicleSection
            title="2018 - Present"
            variant="modern"
            makes={modernMakes}
            models={modernModels}
            trims={modernTrims}
            years={modernYears}
            selectedMake={modernMake}
            selectedModel={modernModel}
            selectedTrim={modernTrim}
            selectedYear={modernYear}
            onSelectMake={onModernSelectMake}
            onSelectModel={onModernSelectModel}
            onSelectTrim={onModernSelectTrim}
            onSelectYear={setModernYear}
            onBuild={handleModernBuild}
            popularBuilds={modernPopularBuilds}
            onPopularBuildClick={(build) =>
              handlePopularBuildClick(build, "modern")
            }
            loading={modernLoading}
          />
        )}
        */}
        </TabsContainer>
      </Body>
      {/* <VerticalSpace height={40} /> */}
      <Footer>
        <IconWrapper>
          <Info />
        </IconWrapper>
        Don't see your vehicle? Give us a call 888-460-0640
      </Footer>
    </Container>
  )
}

export default CarSelector
